import React, { useEffect, useRef, useState } from "react";
import BoxWithIcon from "src/components/molecules/box-with-icon/box-with-icon";
import { IApplicationModel } from "src/types/models/apiModels";
import { ReactComponent as ComputerIcon } from "../../../assets/svg/computerIcon.svg";
import { ReactComponent as MobileIcon } from "../../../assets/svg/mobileIcon.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/svg/downloadIcon.svg";
import { ReactComponent as ClosedEnvelopeIcon } from "../../../assets/svg/closedEnvelopeIcon.svg";
import ButtonPro, {
	ButtonVariant,
} from "src/components/atoms/primary-button/primary-button";
import emailSentAnimationData from "../../../assets/animations/emailSentAnimation.json";
import downloadAnimationData from "../../../assets/animations/downloadAnimation.json";
import Lottie from "lottie-react";
import { ApplicationDownloadsViewModel } from "./application-downloads-view-model";
import { useAuth } from "../auth/auth-context";
import Banner from "src/components/molecules/banner/Banner";

interface IApplicationDownloadsProps {
	application: IApplicationModel;
	onClose: () => void;
}

const ApplicationDownloads: React.FC<IApplicationDownloadsProps> = ({
	application,
	onClose,
}) => {
	const { user, logout } = useAuth();
	const [emailSent, setEmailSent] = useState(false);
	const [downloaded, setDownloaded] = useState(false);
	const [showEmailSentInfo, setShowEmailSentInfo] = useState(false);
	const [showDownloadedInfo, setShowDownloadedInfo] = useState(false);
	const [downloadediOSfileName, setDownloadediOSfileName] = useState("");
	const [mobileAppSecretRecipientNames, setMobileAppSecretRecipientNames] =
		useState([""]);
	const [
		downloadedContactCenterFileName,
		setDownloadedContactCenterFileName,
	] = useState("");
	const [downloadedAndroidfileName, setDownloadedAndroidfileName] =
		useState("");
	const viewModel = useRef<ApplicationDownloadsViewModel | null>(null);

	useEffect(() => {
		if (!user || !user.accessToken) {
			logout();
			return;
		}

		viewModel.current = new ApplicationDownloadsViewModel(user.accessToken);
	});

	const handleMobileAppConfigDownload = async () => {
		setShowDownloadedInfo(true);
		try {
			// Ensure the viewModel is initialized
			if (!viewModel.current) {
				console.error("ViewModel not initialized");
				return;
			}

			// Fetch the URLs
			const apiResponse =
				await viewModel.current?.getUrlsToDownloadAppConfigsAsync(
					application.brandingApplication.id
				);

			console.log(`got response ${JSON.stringify(apiResponse)}`);

			if (apiResponse && apiResponse.result) {
				// Get the URLs from the response
				const { iOSFileUrl, androidFileUrl } = apiResponse.result;

				// Helper function to trigger the download
				const downloadFile = (url: string): string | null => {
					console.log(`downloadFile ${url}`);
					if (url) {
						// Extract file name from the URL (ignore query parameters)
						const fileName =
							url.split("/").pop()?.split("?")[0] || "download"; // Extract file name without query parameters
						const anchor = document.createElement("a");
						anchor.href = url;
						anchor.download = fileName;
						document.body.appendChild(anchor);
						anchor.click();
						document.body.removeChild(anchor);
						return fileName; // Return the extracted file name
					} else {
						console.error("Download URL is empty or undefined");
						return null; // Return null if the URL is invalid
					}
				};

				console.log(`before downloading`);
				// Download iOS and Android files sequentially with a delay
				if (iOSFileUrl) {
					console.log(`downloading iOS using ${iOSFileUrl}`);
					const fileName = downloadFile(iOSFileUrl);

					if (fileName) {
						setDownloadediOSfileName(fileName);
					}
					await new Promise((resolve) => setTimeout(resolve, 2000)); // Add a 2-second delay
				}

				setDownloaded(true);

				if (androidFileUrl) {
					console.log(`downloading android using ${androidFileUrl}`);
					const fileName = downloadFile(androidFileUrl);
					if (fileName) setDownloadedAndroidfileName(fileName);
				}
			} else if (apiResponse?.error) {
				// Show download failed error
				console.error("Error fetching URLs:", apiResponse.error);
			}
		} catch (error) {
			console.error("Error during download process:", error);
		}
	};

	const handleContactCenterConfigDownload = async () => {
		setShowDownloadedInfo(true);
		setDownloaded(true);
		try {
			// Ensure the viewModel is initialized
			if (!viewModel.current) {
				console.error("ViewModel not initialized");
				return;
			}

			// Fetch the URLs
			const apiResponse =
				await viewModel.current?.getUrlsToDownloadContactCenterConfigsAsync(
					application.contactCentre.id
				);

			console.log(`got response ${JSON.stringify(apiResponse)}`);

			if (apiResponse && apiResponse.result) {
				// Get the URLs from the response
				const { configUrl } = apiResponse.result;

				// Helper function to trigger the download
				const downloadFile = (url: string): string | null => {
					console.log(`downloadFile ${url}`);
					if (url) {
						// Extract file name from the URL (ignore query parameters)
						const fileName =
							url.split("/").pop()?.split("?")[0] || "download"; // Extract file name without query parameters
						const anchor = document.createElement("a");
						anchor.href = url;
						anchor.download = fileName;
						document.body.appendChild(anchor);
						anchor.click();
						document.body.removeChild(anchor);
						return fileName; // Return the extracted file name
					} else {
						console.error("Download URL is empty or undefined");
						return null; // Return null if the URL is invalid
					}
				};

				console.log(`before downloading`);
				// Download iOS and Android files sequentially with a delay
				if (configUrl) {
					console.log(`downloading iOS using ${configUrl}`);
					const fileName = downloadFile(configUrl);

					if (fileName) {
						setDownloadedContactCenterFileName(fileName);
					}
					await new Promise((resolve) => setTimeout(resolve, 2000)); // Add a 2-second delay
				}

				setDownloaded(true);
			} else if (apiResponse?.error) {
				// Show download failed error
				console.error("Error fetching URLs:", apiResponse.error);
			}
		} catch (error) {
			console.error("Error during download process:", error);
		}
	};

	const handleDispatchMobileAppSecret = async () => {
		setShowEmailSentInfo(true);
		setEmailSent(false);

		try {
			// Ensure the viewModel is initialized
			if (!viewModel.current) {
				console.error("ViewModel not initialized");
				return;
			}

			// Fetch the URLs
			const apiResponse = await viewModel.current?.dispatchSecret(
				"MobileApp",
				application.business.businessId,
				application.brandingApplication.id
			);

			console.log(`got response ${JSON.stringify(apiResponse)}`);

			if (apiResponse && apiResponse.result) {
				// Get the URLs from the response
				const { recipientNames } = apiResponse.result;
				setEmailSent(true);
				setMobileAppSecretRecipientNames(recipientNames);
			}
		} catch (error) {
			console.error("Error during download process:", error);
		}
	};

	const handleDispatchContactCenterAppSecret = async () => {
		console.log(`handleDispatchContactCenterAppSecret`);

		setShowEmailSentInfo(true);
		setEmailSent(false);

		try {
			// Ensure the viewModel is initialized
			if (!viewModel.current) {
				console.error("ViewModel not initialized");
				return;
			}

			// Fetch the URLs
			const apiResponse = await viewModel.current?.dispatchSecret(
				"ContactCenter",
				application.business.businessId,
				application.contactCentre.id
			);

			console.log(`got response ${JSON.stringify(apiResponse)}`);

			if (apiResponse && apiResponse.result) {
				// Get the URLs from the response
				const { recipientNames } = apiResponse.result;
				setEmailSent(true);
				setMobileAppSecretRecipientNames(recipientNames);
			}
		} catch (error) {
			console.error("Error during download process:", error);
		}
	};

	return (
		<Banner label={"Download configurations and request secrets"}>
			<div className="font-Poppins bg-white flex flex-col items-center justify-center gap-12 p-6 pb-2 rounded-2xl">
				<div className=" flex items-center justify-center gap-4 w-full">
					<BoxWithIcon
						icon={<MobileIcon className="w-4 h-4"></MobileIcon>}
						title={application.brandingApplication.name}
					>
						<div className="flex flex-col items-center justify-start gap-4 p-2 rounded-full w-full">
							<button
								onClick={handleMobileAppConfigDownload}
								className="w-full p-2 px-3 text-sm font-Poppins font-semibold text-white drop-shadow-lg border-2 border-gray-200"
							>
								<div className="w-full flex items-center justify-center gap-1">
									<DownloadIcon className="w-6 h-6 text-gray-900"></DownloadIcon>
									<span className="text-nowrap uppercase text-center text-gray-900">
										Mobile app config
									</span>
								</div>
							</button>
							<button
								onClick={handleDispatchMobileAppSecret}
								className="w-full p-2 px-3 text-sm font-Poppins font-semibold text-white drop-shadow-lg border-2 border-gray-200"
							>
								<div className="w-full flex items-center justify-center gap-1">
									<ClosedEnvelopeIcon className="w-6 h-6 text-gray-900"></ClosedEnvelopeIcon>
									<span className="text-nowrap uppercase text-center text-gray-900">
										Request mobile app secret
									</span>
								</div>
							</button>
						</div>
					</BoxWithIcon>
					{application.contactCentre && (
						<BoxWithIcon
							icon={
								<ComputerIcon className="w-4 h-4"></ComputerIcon>
							}
							title={application.contactCentre.name}
						>
							<div className="flex flex-col items-center justify-start gap-4 p-2 rounded-full">
								<button
									onClick={handleContactCenterConfigDownload}
									className="w-full p-2 px-3 text-sm font-Poppins font-semibold text-white drop-shadow-lg border-2 border-gray-200"
								>
									<div className="w-full flex items-center justify-center gap-1">
										<DownloadIcon className="w-6 h-6 text-gray-900"></DownloadIcon>
										<span className="text-nowrap uppercase text-center text-gray-900">
											Contact center config
										</span>
									</div>
								</button>
								<button
									onClick={
										handleDispatchContactCenterAppSecret
									}
									className="w-full p-2 px-3 text-sm font-Poppins font-semibold text-white drop-shadow-lg border-2 border-gray-200"
								>
									<div className=" w-full flex items-center justify-center gap-1">
										<ClosedEnvelopeIcon className="w-6 h-6 text-gray-900"></ClosedEnvelopeIcon>
										<span className="text-nowrap uppercase text-center text-gray-900">
											Request contact center secret
										</span>
									</div>
								</button>
							</div>
						</BoxWithIcon>
					)}
				</div>

				{showEmailSentInfo && (
					<div className="w-full p-2 flex flex-col items-center justify-center gap-2">
						<Lottie
							autoplay
							loop={!emailSent}
							animationData={emailSentAnimationData}
							style={{ height: "100px", width: "200px" }}
						/>
						{emailSent && mobileAppSecretRecipientNames && (
							<>
								<span className="font-Poppins font-semibold text-nowrap text-center text-gray-900">
									Your secret has been emailed to ...
								</span>
								<div className="max-w-full flex items-center justify-center gap-2 ">
									{mobileAppSecretRecipientNames &&
										mobileAppSecretRecipientNames.map(
											(name, index) => (
												<span
													key={index}
													className="block font-Poppins font-semibold text-nowrap text-center text-gray-900"
												>
													{name}
												</span>
											)
										)}
								</div>
							</>
						)}
					</div>
				)}

				{showDownloadedInfo && (
					<div className="w-full p-2 flex flex-col items-center justify-center gap-2">
						<Lottie
							autoplay
							loop={!downloaded}
							animationData={downloadAnimationData}
							style={{ height: "100px", width: "200px" }}
						/>

						{downloaded && (
							<>
								{downloadediOSfileName && (
									<span className="font-Poppins font-semibold text-nowrap text-center text-gray-900">
										iOS Config file :{" "}
										{downloadediOSfileName}
									</span>
								)}
								{downloadedAndroidfileName && (
									<span className="font-Poppins font-semibold text-nowrap text-center text-gray-900">
										Android Config file :
										{downloadedAndroidfileName}
									</span>
								)}
								{downloadedContactCenterFileName && (
									<>
										<span className="font-Poppins font-semibold text-nowrap text-center text-gray-900">
											Contact center Config file :
											{downloadedContactCenterFileName}
										</span>
									</>
								)}
							</>
						)}
					</div>
				)}

				<ButtonPro
					variant={ButtonVariant.Secondary}
					label={"Close"}
					onClick={onClose}
				></ButtonPro>
			</div>
		</Banner>
	);
};

export default ApplicationDownloads;
